<template>
  <div v-if="newAssetMode">
    <Field symbol="category_id" class="pt-4">
      <ZLabel for="category_id">
        {{ $t('asset.form.category') }}
      </ZLabel>
      <SelectTree v-model="selectedCategory" :tree="categoriesTree"/>
    </Field>
    <Field class="py-4" symbol="asset_model_code">
      <ZLabel for="code">
        {{ $t('asset.form.model_number') }}
      </ZLabel>
      <ZInput
        id="code"
        v-model="asset.code"
        :placeholder="$t('asset.label.model_search')"
      />
    </Field>
    <Field symbol="manufacturer" class="pt-4">
      <ZLabel for="manufacturer">
        {{ $t('asset.form.manufacturer') }}
      </ZLabel>
      <ZInput id="name" v-model="asset.manufacturer"/>
    </Field>
  </div>
  <div v-else>
    <Field class="py-4" symbol="model_search">
      <ZLabel for="model_search">
        {{ $t('asset.form.model') }}
      </ZLabel>
      <ZInput
        id="model_search"
        v-model="filters.search"
        :placeholder="$t('asset.label.model_search')"
      />
    </Field>

    <div class="flex gap-2">
      <Field symbol="category_id" class="pb-4">
        <ZLabel for="category_id">
          {{ $t('asset.form.category') }}
        </ZLabel>
        <SelectTree v-model="selectedCategory" :tree="categoriesTree"/>
      </Field>

      <Field symbol="scope" class="pb-4">
        <ZLabel for="scope">
          {{ $t('asset.form.scope') }}
        </ZLabel>
        <SelectField
          id="type"
          v-model="filters.type"
          :placeholder="$t('asset.form.select_scope')"
          :options="scopes"
          translate="assetModels.scopes."
        />
      </Field>
    </div>


    <span class="text-sm">Wybierz model:</span>
    <div class="border rounded h-[29rem] mt-1">
      <ZTable>
        <ZTableHeader>
          <ZTableRow>
            <ZTableHead class="w-[50px]"></ZTableHead>
            <ZTableHead class="w-[80px]"></ZTableHead>
            <ZTableHead>Model</ZTableHead>
            <ZTableHead>Producent</ZTableHead>
          </ZTableRow>
        </ZTableHeader>
        <ZTableBody>
          <ZTableRow v-for="(model, index) in models.data" :key="index">
            <ZTableCell>
              <Checkbox
                id="is_required"
                class="h-6 w-6"
                :checked="selectedModel == model.id"
                @update:checked="toggleSelected(model.id, model.code)"
              />
            </ZTableCell>
            <ZTableCell>
              <img
                v-if="model.thumb"
                class="w-[50px] h-[50px] object-contain rounded-xl"
                :src="model.thumb"
                alt="primaryImage"
              />
            </ZTableCell>
            <ZTableCell class="flex flex-col"><b>{{ model.code }}</b><span>{{ model.name }}</span></ZTableCell>
            <ZTableCell>{{ model.manufacturer ? model.manufacturer : '-' }}</ZTableCell>
          </ZTableRow>
        </ZTableBody>
      </ZTable>
      <div v-if="models.data?.length == 0" class="p-8 text-center flex flex-col gap-2 items-center">
        <ExclamationTriangleIcon class="icon lg"/>
        {{ $t('asset.label.create_new_model') }}
        <ZButton @click="newAssetMode = true" v-if="newAsset">{{ $t('asset.command.create_new_model') }}</ZButton>
      </div>
    </div>
    <div class="min-h-[5vh]">
      <Paginator :data="models"/>
    </div>
    <Field class="py-4" symbol="model_clone">
      <ZLabel for="model_clone" class=" flex flex-row gap-2 items-center">
        <ZSwitch
          id="model_clone"
          @update:checked="val => asset.model_clone = val"
        />
        {{ $t('asset.form.model_clone') }}
      </ZLabel>
    </Field>
    <Field symbol="code" v-if="asset.model_clone">
      <ZLabel for="model_code" class=" flex flex-row gap-2 items-center">
        {{ $t('asset.label.asset_model') }}
      </ZLabel>
      <ZInput v-model="asset.code"/>
    </Field>
  </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue';
import {ZInput} from '@shadcn/components/ui/input';
import {ZLabel} from '@shadcn/components/ui/label';
import {ZButton} from "@shadcn/components/ui/button";
import {ZSwitch} from "@shadcn/components/ui/switch";
import Field from '@ui/FormField.vue';
import Paginator from '@ui/Paginator.vue';
import {PropType} from 'vue';
import {DataSet} from '@/utils/DataSet';
import SelectTree from '@ui/SelectTree.vue';
import {ZTable, ZTableBody, ZTableCell, ZTableHead, ZTableHeader, ZTableRow} from "@shadcn/components/ui/table";
import {Checkbox} from "@shadcn/components/ui/checkbox";
import {ExclamationTriangleIcon} from "@modules/@heroicons/vue/24/outline";
import SelectField from "@ui/SelectField.vue";

export default defineComponent({
  components: {
    SelectField,
    ExclamationTriangleIcon,
    Checkbox,
    ZTableHead,
    ZTableBody,
    ZTableCell,
    ZTableHeader,
    ZTableRow,
    ZTable,
    SelectTree,
    Paginator,
    ZInput,
    ZLabel,
    ZButton,
    ZSwitch,
    Field,
  },

  props: {
    asset: {
      type: Object,
      required: true,
    },

    filters: {
      type: Object,
      required: true,
    },

    categories: {
      type: Object,
      required: true,
    },

    models: {
      type: Object as PropType<DataSet<any, any>>,
      required: true,
    },

    newAsset: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      selectedCategory: '' as string,
      selectedModel: '' as string,
      newAssetMode: false as boolean,
      scopes: {} as KeyValue
    };
  },

  computed: {
    categoriesTree() {
      let tree = new Map<string, object>();

      for (let index in this.categories) {
        const category = this.categories[index];
        const treeKey = category.id.toString();
        tree.set(treeKey, {depth: category.depth, label: category.name});
      }

      return tree;
    },
  },

  watch: {
    selectedCategory(val: string) {
      this.asset.asset_model_id = null;
      this.asset.category_id = parseInt(val);
    },

    selectedModel(val: string) {
      this.asset.asset_model_id = parseInt(val);
    },

    'filters.search'() {
      this.selectedModel = '';
      this.asset.asset_model_id = null;
      this.asset.code = this.filters.search;
    },
  },

  mounted() {
    if (this.asset.category_id) this.selectedCategory = this.asset.category_id?.toString() ?? '';
    if (this.asset.asset_model_id) this.selectedModel = this.asset.asset_model_id?.toString() ?? '';
    this.scopes = {
      'all': 'all',
      'organization': 'organization',
      'global': 'global'
    };
  },

  methods: {
    toggleSelected(id: string, code: string) {
      if (this.selectedModel !== id) {
        this.selectedModel = id
        this.asset.code = code;
      } else {
        this.selectedModel = ''
        this.asset.code = '';
      }
    }
  }
});
</script>
